import { ElementProps } from '@/components/sections/builder/types';
import InputSlider from '@/components/sections/builder/settings/input-slider';

import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { CollageElementDataType } from '@/components/sections/builder/elements/body/collage/type';

import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';

const HEADING_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extraLarge', label: 'Extra large' },
  { value: 'extraExtraLarge', label: 'Extra extra large' },
];

const CARD_STYLE_OPTIONS = [
  { value: 'all', label: 'Style all as product cards' },
  { value: 'individual', label: 'Use individual card styles' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as CollageElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Heading Settings</h1>
      <div className='mb-3'>
        <h4 className='mb-1'>Heading Label</h4>
        <TextEditorMinimal
          placeholder='Label'
          onChange={(value) =>
            builder.setElementData(element.id, {
              headingLabel: value,
            })
          }
          value={data.headingLabel}
          className='mb-3 w-full '
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-1',
            },
          }}
          editable
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Size</h4>
        <InputSelect
          defaultValue={data.headingSize}
          options={HEADING_SIZE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(v) => builder.setElementData(element.id, { headingSize: v })}
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Desktop Layout</h4>
        <InputSelect
          defaultValue={data.desktopLayout}
          options={[
            { label: 'Large Left Block', value: 'largeLeftBlock' },
            { label: 'Large Right Block', value: 'largeRightBlock' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { desktopLayout: v })}
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Mobile Layout</h4>
        <InputSelect
          defaultValue={data.mobileLayout}
          options={[
            { label: 'Collage', value: 'collage' },
            { label: 'Column', value: 'column' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { mobileLayout: v })}
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Card Style</h4>
        <InputSelect
          defaultValue={data.cardStyle}
          options={CARD_STYLE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(v) => builder.setElementData(element.id, { cardStyle: v })}
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Content Alignment</h4>
        <InputSelect
          defaultValue={data.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>
      <div className='mb-1 text-xs md:mt-4'>Color Scheme</div>
      <SchemeSetting
        value={data.colorSchemeCollageSection}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorSchemeCollageSection: theme,
          });
        }}
      />
      <InputSlider
        label='Top padding'
        value={data.topPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            topPadding: val,
          })
        }
      />
      <InputSlider
        label='Bottom padding'
        value={data.bottomPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            bottomPadding: val,
          })
        }
      />
    </div>
  );
}
